<script setup lang="ts">
import type { TraderOfficialToken, TraderOfficialTokenTab } from '~/server/detail'
import Up from '@/assets/icon/up.svg'
import Down from '@/assets/icon/down.svg'
const props = defineProps<{
  officialToken?: TraderOfficialToken | null
  exchangeName?: string
  exchangeId?: string
}>()
const matomo = useMatomo()

const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const officialTokenTitle = computed(
  () => normalizeText(props.officialToken?.title || props.officialToken?.topicTitle) || '官方代币',
)

const officialTokenTabs = computed(() => props.officialToken?.tabs || [])
const showOfficialTokenTabs = computed(() => officialTokenTabs.value.length > 1)

const activeToken = ref('')

watch(
  officialTokenTabs,
  (tabs) => {
    const activeExists = tabs.some((tab) => normalizeText(tab.title) === activeToken.value)

    if (activeExists) {
      return
    }

    activeToken.value = normalizeText(tabs[0]?.title)
  },
  {
    immediate: true,
  },
)

const currentToken = computed<TraderOfficialTokenTab | null>(() => {
  const tabs = officialTokenTabs.value
  const active = tabs.find((tab) => normalizeText(tab.title) === activeToken.value)

  return active || tabs[0] || null
})

const officialTokenMetrics = computed(() => currentToken.value?.infos || [])

const officialTokenChangeClass = computed(() => {
  const tokenChange = normalizeText(currentToken.value?.tokenChange)

  if (tokenChange.startsWith('-')) {
    return 'exchange-officialtoken__price-change-down'
  }

  if (tokenChange) {
    return 'exchange-officialtoken__price-change-up'
  }

  return ''
})

const switchOfficialToken = (tab: TraderOfficialTokenTab) => {
  const tabName = normalizeText(tab.title)

  activeToken.value = tabName
  matomo.trackEvent('broker_column_token', {
    af_type: tabName,
    af_name: normalizeText(props.exchangeName),
    af_code: normalizeText(props.exchangeId),
  })
}
</script>

<template>
  <section class="exchange-officialtoken" aria-labelledby="exchange-officialtoken-title">
    <div class="exchange-officialtoken__header">
      <h2 id="exchange-officialtoken-title" class="exchange-officialtoken__title">{{ officialTokenTitle }}</h2>

      <div v-if="showOfficialTokenTabs" class="exchange-officialtoken__tabs" role="tablist" aria-label="官方代币切换">
        <button
          v-for="tab in officialTokenTabs"
          :key="tab.title"
          type="button"
          class="exchange-officialtoken__tab"
          :class="{ 'exchange-officialtoken__tab-active': tab.title === activeToken }"
          :aria-selected="tab.title === activeToken"
          @click="switchOfficialToken(tab)"
        >
          {{ tab.title }}
        </button>
      </div>
    </div>

    <div v-if="currentToken" class="exchange-officialtoken__summary">
      <div class="exchange-officialtoken__asset">
        <CommonImage
          v-if="currentToken.exchangeLogo"
          class="exchange-officialtoken__asset-icon"
          :src="currentToken.exchangeLogo"
          :alt="currentToken.exchangeShortName || currentToken.title || 'token'"
        />

        <div v-else class="exchange-officialtoken__asset-icon exchange-officialtoken__asset-icon-fallback">
          {{ currentToken.exchangeShortName || currentToken.title }}
        </div>

        <div class="exchange-officialtoken__asset-copy">
          <p class="exchange-officialtoken__asset-name">{{ currentToken.exchangeShortName || currentToken.title }}</p>
          <p class="exchange-officialtoken__asset-full-name">{{ currentToken.exchangeFullName || '--' }}</p>
        </div>
      </div>

      <div
        v-if="currentToken.tokenPrice && currentToken.tokenPrice !== '--'"
        class="exchange-officialtoken__price-wrap"
      >
        <p class="exchange-officialtoken__price">{{ currentToken.tokenPrice || '--' }}</p>
        <div class="flex items-center">
          <p class="exchange-officialtoken__price-change" :class="officialTokenChangeClass">
            {{ currentToken.tokenChange || '--' }}
          </p>
          <Up
            v-if="currentToken.changeType === 2"
            class="change-icon exchange-supported-currencies-card__change-icon-up"
            aria-hidden="true"
          />
          <Down v-else class="change-icon exchange-supported-currencies-card__change-icon-down" aria-hidden="true" />
        </div>
      </div>
    </div>

    <div class="exchange-officialtoken__metrics">
      <article v-for="metric in officialTokenMetrics" :key="metric.title" class="exchange-officialtoken__metric">
        <p class="exchange-officialtoken__metric-label">{{ metric.title }}</p>
        <p class="exchange-officialtoken__metric-value">{{ metric.content || '--' }}</p>
      </article>
    </div>
  </section>
</template>

<style scoped lang="less">
.exchange-officialtoken {
  margin-top: 40px;
  border: 1px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: #ffffff;
  padding: 24px;
}

.exchange-officialtoken__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.exchange-officialtoken__title {
  margin: 0;
  color: #11151c;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  width: 100%;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  padding-bottom: 16px;
}

.exchange-officialtoken__tabs {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  cursor: pointer;
}

.exchange-officialtoken__tab {
  min-width: 28px;
  border: none;
  border-radius: 2px;
  color: #7d818a;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  padding: 5px 12px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  background: #fff;
}

.exchange-officialtoken__tab-active {
  background: #1f3f93;
  color: #ffffff;
}

.exchange-officialtoken__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 16px;
}

.exchange-officialtoken__asset {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.exchange-officialtoken__asset-icon {
  width: 40px;
  height: 40px;
  flex: none;
  object-fit: contain;
}

.exchange-officialtoken__asset-icon-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f1f4fb;
  color: #1f3f93;
  font-size: 12px;
  font-weight: 600;
}

.exchange-officialtoken__asset-copy {
  min-width: 0;
}

.exchange-officialtoken__asset-name,
.exchange-officialtoken__asset-full-name,
.exchange-officialtoken__price,
.exchange-officialtoken__price-change,
.exchange-officialtoken__metric-label,
.exchange-officialtoken__metric-value {
  margin: 0;
}

.exchange-officialtoken__asset-name {
  color: #11151c;
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
}

.exchange-officialtoken__asset-full-name {
  margin-top: 2px;
  color: #adb0b8;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.exchange-officialtoken__price-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 24px;
}

.exchange-officialtoken__price {
  color: #11151c;
  font-size: 20px;
  font-weight: 600;
  line-height: 32px;
  white-space: nowrap;
}

.exchange-officialtoken__price-change {
  color: #7d818a;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.exchange-officialtoken__price-change-up {
  color: #dd4b39;
}

.exchange-officialtoken__price-change-down {
  color: #11a683;
}

.exchange-officialtoken__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px 24px;
  margin-top: 20px;
}

.exchange-officialtoken__metric {
  min-height: 38px;
  border-radius: 2px;
  background: #f8f8f8;
  padding: 8px 12px;
}

.exchange-officialtoken__metric-label {
  color: #7d818a;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.exchange-officialtoken__metric-value {
  margin-top: 4px;
  color: #292a2e;
  direction: ltr;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-align: right;
  unicode-bidi: isolate;
  word-break: break-word;
}

.exchange-supported-currencies-card__change-icon-up {
  animation: exchange-supported-currencies-rise 1.5s ease-in-out infinite;
}

.exchange-supported-currencies-card__change-icon-down {
  animation: exchange-supported-currencies-fall 1.5s ease-in-out infinite;
}

@keyframes exchange-supported-currencies-rise {
  0%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }

  50% {
    transform: translateY(-3px);
    opacity: 0.82;
  }
}

@keyframes exchange-supported-currencies-fall {
  0%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }

  50% {
    transform: translateY(3px);
    opacity: 0.82;
  }
}

@media (prefers-reduced-motion: reduce) {
  .exchange-supported-currencies-card__change-icon-up,
  .exchange-supported-currencies-card__change-icon-down {
    animation: none;
  }
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .exchange-officialtoken__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 799px) {
  .exchange-officialtoken {
    padding: 20px;
  }

  .exchange-officialtoken__summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .exchange-officialtoken__price-wrap {
    padding-left: 0;
  }

  .exchange-officialtoken__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
